import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

export default function LoanEligibility() {

  const [income, setIncome] =
    useState("");

  const [eligibility,
    setEligibility] =
    useState(null);

  const [error,
    setError] =
    useState("");

  const calculate = () => {

    setError("");

    if (!income || income <= 0) {
      setEligibility(null);

      setError(
        "Please enter valid monthly income"
      );

      return;
    }

    const result =
      Number(income) * 60;

    setEligibility(result);
  };

  return (
    <>
      <SEO
        title="Loan Eligibility Calculator - Check Loan Eligibility Online"
        description="Use our free Loan Eligibility Calculator to check home loan, personal loan, and car loan eligibility instantly based on monthly income."
        keywords="loan eligibility calculator, home loan eligibility, personal loan eligibility, car loan calculator, loan calculator"
        url="https://online-tools-center.vercel.app/loan-eligibility"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-9">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              {/* HERO */}

              <div className="text-center mb-4">

                <h1 className="fw-bold mb-3">
                  Loan Eligibility Calculator
                </h1>

                <p className="text-muted">
                  Check your estimated loan eligibility
                  instantly based on your monthly income.
                  Useful for home loans, car loans, and
                  personal loans.
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">

                  <span className="badge bg-light text-dark p-2">
                    ✓ Instant Eligibility Check
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Free Loan Tool
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Secure Calculation
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Mobile Friendly
                  </span>

                </div>

              </div>

              {/* MAIN CONTENT */}

              <div className="row g-4 align-items-center">

                {/* LEFT */}

                <div className="col-md-6">

                  <label className="fw-semibold mb-2">
                    Monthly Income
                  </label>

                  <input
                    type="number"
                    placeholder="Enter Monthly Income"
                    className="form-control mb-3"
                    value={income}
                    onChange={(e) =>
                      setIncome(e.target.value)
                    }
                  />

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold"
                    onClick={calculate}
                  >
                    Check Eligibility
                  </button>

                  {error && (
                    <div className="alert alert-danger mt-3">
                      {error}
                    </div>
                  )}

                  {eligibility && (
                    <div className="alert alert-success mt-4">

                      <h5 className="mb-2">
                        Estimated Loan Eligibility
                      </h5>

                      <h2 className="fw-bold">
                        ₹ {eligibility.toLocaleString()}
                      </h2>

                    </div>
                  )}

                </div>

                {/* RIGHT */}

                <div className="col-md-6">

                  <div className="bg-light rounded-4 p-4">

                    <h4 className="fw-bold mb-3">
                      Factors Affecting Loan Eligibility
                    </h4>

                    <ul className="mb-0">

                      <li>
                        Monthly income
                      </li>

                      <li>
                        Existing EMIs
                      </li>

                      <li>
                        Credit score
                      </li>

                      <li>
                        Employment stability
                      </li>

                      <li>
                        Loan tenure
                      </li>

                      <li>
                        Interest rate
                      </li>

                    </ul>

                  </div>

                </div>

              </div>

            </div>

            {/* SEO CONTENT */}

            <div className="mt-5">

              <h2 className="fw-bold mb-3">
                What is Loan Eligibility?
              </h2>

              <p>
                Loan eligibility refers to the
                maximum loan amount a borrower can
                obtain based on income, repayment
                capacity, credit score, and other
                financial factors.
              </p>

              <p>
                Banks and financial institutions
                calculate loan eligibility to ensure
                borrowers can comfortably repay the
                loan without financial stress.
              </p>

              <h2 className="fw-bold mt-5 mb-3">
                How Loan Eligibility is Calculated
              </h2>

              <p>
                Loan eligibility is generally based
                on monthly income and repayment
                capacity. Most lenders estimate loan
                eligibility using a multiplier of
                annual income along with existing
                liabilities and credit profile.
              </p>

              <p>
                Higher income and lower debts usually
                improve your chances of getting a
                larger loan amount at better interest
                rates.
              </p>

              <h2 className="fw-bold mt-5 mb-3">
                Benefits of Using Loan Eligibility Calculator
              </h2>

              <ul>

                <li>
                  Instant loan estimation
                </li>

                <li>
                  Better financial planning
                </li>

                <li>
                  Compare borrowing capacity
                </li>

                <li>
                  Helps prepare for loan applications
                </li>

                <li>
                  Free and secure online tool
                </li>

              </ul>

              <h2 className="fw-bold mt-5 mb-3">
                Types of Loans Supported
              </h2>

              <ul>

                <li>
                  Home Loan Eligibility
                </li>

                <li>
                  Personal Loan Eligibility
                </li>

                <li>
                  Car Loan Eligibility
                </li>

                <li>
                  Business Loan Eligibility
                </li>

              </ul>

              <h2 className="fw-bold mt-5 mb-3">
                Frequently Asked Questions
              </h2>

              <div className="mb-4">

                <h5>
                  What affects loan eligibility?
                </h5>

                <p>
                  Income, credit score, existing EMIs,
                  employment history, and loan tenure
                  affect eligibility.
                </p>

              </div>

              <div className="mb-4">

                <h5>
                  Does credit score matter?
                </h5>

                <p>
                  Yes, a higher credit score improves
                  approval chances and may help secure
                  lower interest rates.
                </p>

              </div>

              <div className="mb-4">

                <h5>
                  Is this calculator accurate?
                </h5>

                <p>
                  This calculator provides an estimate.
                  Actual loan approval depends on bank
                  policies and financial verification.
                </p>

              </div>

              <div className="mb-4">

                <h5>
                  Is this tool free to use?
                </h5>

                <p>
                  Yes, this online loan eligibility
                  calculator is completely free and
                  works on mobile and desktop devices.
                </p>

              </div>

              {/* RELATED TOOLS */}

              <div className="mt-5 p-4 bg-light rounded-4">

                <h3 className="fw-bold mb-3">
                  Related Financial Tools
                </h3>

                <div className="d-flex flex-wrap gap-3">

                  <a
                    href="/emi-calculator"
                    className="btn btn-outline-primary"
                  >
                    EMI Calculator
                  </a>

                  <a
                    href="/tax-calculator"
                    className="btn btn-outline-primary"
                  >
                    Tax Calculator
                  </a>

                  <a
                    href="/percentage-calculator"
                    className="btn btn-outline-primary"
                  >
                    Percentage Calculator
                  </a>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      <Footer />
    </>
  );
}